/**
 * Preview-branch SSO shim (browser side).
 *
 * An ephemeral preview's own GoTrue can't finish an Okta sign-in: Okta only
 * accepts the dev/staging/prod callback addresses, so a preview signs in
 * through **dev** and then converts that identity into a session on its own
 * project. See docs/plans/PREVIEW_BRANCH_AUTH_SHIM.md.
 *
 * Everything here is a pure function of the environment, the hostname and the
 * URL, so the decisions can be tested without a browser. The I/O lives in
 * src/components/PreviewAuthShim.tsx.
 *
 * `PREVIEW_ORIGIN_RE` is mirrored in
 * supabase/functions/preview-auth-shim/shim-rules.ts. Change both together.
 */

/** PR preview deploys of this app: pr-<number>.tms.aurora.tech. */
export const PREVIEW_ORIGIN_RE = /^pr-\d+\.tms\.aurora\.tech$/;

export const SHIM_FUNCTION_SLUG = "preview-auth-shim";

export interface PreviewAuthShimConfig {
  /** The dev project the preview signs in through. */
  devUrl: string;
  /** Dev's publishable key (public: it ships in dev's own bundle). */
  devPublishableKey: string;
  /** Dev's custom OIDC provider id, e.g. custom:atms-okta-oidc. */
  provider: string;
}

export type PreviewShimCallback =
  | { kind: "code"; code: string }
  | { kind: "error"; message: string };

export function isPreviewHost(hostname: string): boolean {
  return PREVIEW_ORIGIN_RE.test(hostname);
}

/**
 * The shim is configured by the preview deploy only (`VITE_AUTH_SHIM_*`), and
 * it is used only on a preview host. Both conditions matter: a stray set of
 * these variables on dev/staging/prod must not divert their normal sign-in.
 */
export function getPreviewAuthShimConfig(
  env: Record<string, unknown> | undefined | null,
  hostname: string,
): PreviewAuthShimConfig | null {
  if (!env || !isPreviewHost(hostname)) return null;
  const devUrl = String(env.VITE_AUTH_SHIM_SUPABASE_URL ?? "").trim().replace(/\/+$/, "");
  const devPublishableKey = String(env.VITE_AUTH_SHIM_PUBLISHABLE_KEY ?? "").trim();
  const provider = String(env.VITE_AUTH_OIDC_PROVIDER ?? "").trim();
  if (!devUrl || !devPublishableKey || !provider) return null;
  if (!/^https:\/\/[a-z0-9]+\.supabase\.co$/.test(devUrl)) return null;
  return { devUrl, devPublishableKey, provider };
}

/**
 * Read this build's shim configuration. Kept here, and written as static
 * member access, because Vite only rewrites `import.meta.env.VITE_FOO`
 * literally; a dynamic lookup would leave the values undefined in the bundle.
 */
export function previewAuthShimConfigFor(hostname: string): PreviewAuthShimConfig | null {
  return getPreviewAuthShimConfig(
    {
      VITE_AUTH_SHIM_SUPABASE_URL: import.meta.env.VITE_AUTH_SHIM_SUPABASE_URL,
      VITE_AUTH_SHIM_PUBLISHABLE_KEY: import.meta.env.VITE_AUTH_SHIM_PUBLISHABLE_KEY,
      VITE_AUTH_OIDC_PROVIDER: import.meta.env.VITE_AUTH_OIDC_PROVIDER,
    },
    hostname,
  );
}

/**
 * Read dev's callback out of the URL. Called at module load, because
 * supabase-js strips the query/hash when it first initialises.
 *
 * The shim asks for PKCE, so the expected callback is a `?code=` to redeem. A
 * session in the fragment means the implicit flow ran instead: that is a
 * configuration problem, not a callback to accept. Accepting it would put dev's
 * session in the preview's URL and would hide the misconfiguration behind a
 * sign-in that appears to work.
 */
export function readShimCallback(location: {
  search: string;
  hash: string;
}): PreviewShimCallback | null {
  const query = new URLSearchParams(location.search.replace(/^\?/, ""));
  const fragment = new URLSearchParams(location.hash.replace(/^#/, ""));
  const pick = (key: string) => query.get(key) ?? fragment.get(key) ?? null;

  const error = pick("error");
  if (error) {
    return { kind: "error", message: pick("error_description") ?? error };
  }
  const code = pick("code");
  if (code) {
    return { kind: "code", code };
  }
  if (fragment.get("access_token")) {
    return {
      kind: "error",
      message:
        "Dev returned its session in the URL fragment instead of a PKCE code, so PKCE did not engage. " +
        "Check that the dev project's OIDC provider has PKCE enabled.",
    };
  }
  return null;
}

/** Where the exchange happens: the preview's own project. */
export function shimExchangeUrl(branchSupabaseUrl: string): string {
  return `${branchSupabaseUrl.replace(/\/+$/, "")}/functions/v1/${SHIM_FUNCTION_SLUG}`;
}

/**
 * The exchange carries the *dev* session token (the proof of identity the
 * function verifies) plus this project's publishable key, which the function
 * gateway requires as an apikey.
 */
export function shimExchangeHeaders(
  branchPublishableKey: string,
  devAccessToken: string,
): Record<string, string> {
  return {
    apikey: branchPublishableKey,
    Authorization: `Bearer ${devAccessToken}`,
    "Content-Type": "application/json",
  };
}
